import React from "react";
import type { TestSet } from "./internal_details";

const inputClass = "rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950";
const relativeLabels: Record<string, string> = {
  "After consult": "after this consult",
  "After previous set": "after the previous set",
  "Before surgery": "before surgery",
  "Before follow-up": "before the follow-up visit",
};

export default function LabTiming({ set, index, onChange }: { set: TestSet; index: number; onChange: (patch: Partial<TestSet>) => void }) {
  const relative = relativeLabels[set.when];
  const units = set.interval === "1" ? set.unit.replace(/s$/, "") : set.unit;
  return <div className="grid items-start gap-2 sm:grid-cols-2">
    <div className="min-w-0 space-y-2">
    <label className="block text-sm font-medium">When
      <select aria-label="When should these tests be done?" className={`${inputClass} mt-1 block w-full`} value={set.when} onChange={(event) => {
        const when = event.target.value;
        onChange({ when, ...(!relativeLabels[when] ? { interval: "" } : { unit: set.unit || "weeks" }), ...(when !== "On a date" ? { target_date: "" } : {}) });
      }}>
        <option value="">Select timing</option>
        {["Now", "After consult", "After previous set", "On a date", "Before surgery", "Before follow-up", "Already ordered", "Other"].filter((option) => index > 0 || option !== "After previous set").map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
    {relative && <div>
      <div className="flex flex-wrap items-center gap-1.5 text-sm">
        <input aria-label="Time before or after event" className={`${inputClass} w-20`} value={set.interval} maxLength={100} placeholder="e.g. 1" onChange={(event) => onChange({ interval: event.target.value })} />
        <select aria-label="Time unit" className={inputClass} value={set.unit} onChange={(event) => onChange({ unit: event.target.value })}>
          {["days", "weeks", "months"].map((unit) => <option key={unit} value={unit}>{set.interval === "1" ? unit.replace(/s$/, "") : unit}</option>)}
        </select>
        <span>{set.when === "After previous set" ? `after set ${index}` : relative}.</span>
      </div>
      <p className={set.interval ? "sr-only" : "mt-1 text-xs text-neutral-500"}>{set.interval ? `Tests are planned ${set.interval} ${units} ${set.when === "After previous set" ? `after set ${index}` : relative}.` : "Leave the amount blank if undecided."}</p>
    </div>}
    {!relative && set.interval && <p className="text-sm text-amber-700 dark:text-amber-300">Previously entered interval: {set.interval} {set.unit}. Choose a relative timing option above to clarify what this relates to. <button type="button" className="underline" onClick={() => onChange({ interval: "" })}>Clear interval</button></p>}
    {set.when === "On a date" && <label className="block text-sm">Test date<input type="date" className={`${inputClass} mt-1 block`} value={set.target_date || ""} onChange={(event) => onChange({ target_date: event.target.value })} /></label>}
    {set.when === "Already ordered" && <p className="text-xs text-neutral-500">An order already exists. Add any instructions about when to complete it below.</p>}
    </div>
    <label className="block min-w-0 text-sm">{set.when === "Other" ? "Describe when to do these tests" : "Timing notes (optional)"}
      <input className={`${inputClass} mt-1 block w-full`} maxLength={4000} value={set.timing} placeholder="e.g. After outside results are reviewed" onChange={(event) => onChange({ timing: event.target.value })} />
    </label>
  </div>;
}
